import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './BOMs.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const BOMList = forwardRef(({ onSelectBOM, selectedBOMId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const [boms, setBOMs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchBOMs
  }));

  useEffect(() => {
    fetchBOMs();
  }, [statusFilter, typeFilter]);

  const fetchBOMs = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/boms?page_size=100'));
      if (response.ok) {
        const data = await response.json();
        setBOMs(data.boms || []);
      } else {
        setError(t('boms.list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching BOMs:', error);
      setError(t('boms.list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredBOMs = boms.filter(bom => {
    const matchesSearch = (bom.bom_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          bom.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          bom.description?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || bom.status?.toUpperCase() === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusUpper = status?.toUpperCase();
    let className = 'status-badge ';

    switch (statusUpper) {
      case 'DRAFT':
        className += 'status-warning';
        break;
      case 'ACTIVE':
        className += 'status-success';
        break;
      case 'OBSOLETE':
        className += 'status-error';
        break;
      default:
        className += 'status-inactive';
    }

    const displayText = status ? t(`boms.list.status.${status.toLowerCase()}`) : t('boms.list.status.unknown');
    return <span className={className}>{displayText}</span>;
  };

  const getTypeBadge = (bomType) => {
    const typeUpper = bomType?.toUpperCase();
    let className = 'type-badge ';

    switch (typeUpper) {
      case 'PRODUCTION':
        className += 'type-production';
        break;
      case 'ENGINEERING':
        className += 'type-engineering';
        break;
      case 'SALES':
        className += 'type-sales';
        break;
      default:
        className += 'type-production';
    }

    return <span className={className}>{bomType ? t(`boms.list.types.${bomType.toLowerCase()}`) : t('boms.list.types.production')}</span>;
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && boms.length === 0) {
    return <div className="list-container"><div className="loading">{t('boms.list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('boms.list.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('boms.list.newBOM')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('boms.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('boms.list.filters.allStatus')}</option>
            <option value="DRAFT">{t('boms.list.filters.draft')}</option>
            <option value="ACTIVE">{t('boms.list.filters.active')}</option>
            <option value="OBSOLETE">{t('boms.list.filters.obsolete')}</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('boms.list.filters.allTypes')}</option>
            <option value="production">{t('boms.list.filters.production')}</option>
            <option value="engineering">{t('boms.list.filters.engineering')}</option>
            <option value="sales">{t('boms.list.filters.sales')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('boms.list.headers.bomName')}</th>
              <th>{t('boms.list.headers.product')}</th>
              <th>{t('boms.list.headers.version')}</th>
              <th>{t('boms.list.headers.status')}</th>
              <th>{t('boms.list.headers.yield')}</th>
              <th>{t('boms.list.headers.default')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredBOMs.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {searchTerm ? t('boms.list.noData.noResults') : t('boms.list.noData.noBOMs')}
                </td>
              </tr>
            ) : (
              filteredBOMs.map(bom => (
                <tr
                  key={bom.bom_id}
                  onClick={() => onSelectBOM(bom.bom_id)}
                  className={selectedBOMId === bom.bom_id ? 'selected' : ''}
                >
                  <td className="bom-info">
                    <div className="bom-name">
                      <strong>{bom.bom_name || t('boms.list.defaultValues.unnamedBOM')}</strong>
                    </div>
                  </td>
                  <td className="bom-info">
                    {bom.product_code ? (
                      <div>
                        <strong>{bom.product_code}</strong>
                        {bom.product_name && <div className="bom-info">{bom.product_name}</div>}
                      </div>
                    ) : (
                      <span className="bom-info">{t('boms.list.defaultValues.productNumber')}{bom.product_id || t('boms.list.defaultValues.notAvailable')}</span>
                    )}
                  </td>
                  <td className="text-center">
                    <strong>{bom.bom_version || '1.0'}</strong>
                  </td>
                  <td>
                    {getStatusBadge(bom.status)}
                  </td>
                  <td className="text-center">
                    <strong>{bom.yield_percentage || 100}%</strong>
                  </td>
                  <td className="text-center">
                    {bom.is_default ? (
                      <span className="status-badge status-success">{t('boms.list.defaultValues.yes')}</span>
                    ) : (
                      <span className="text-secondary">{t('boms.list.defaultValues.no')}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
{t('boms.list.footer.recordCount', { filtered: filteredBOMs.length, total: boms.length })}
        </div>
      </div>
    </div>
  );
});

BOMList.displayName = 'BOMList';

export default BOMList;